import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import LaunchIcon from '@mui/icons-material/Launch';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import PictureInPictureAltIcon from '@mui/icons-material/PictureInPictureAlt';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { PopoverProps } from '@mui/material/Popover';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLivePlayerSettingsStore } from '../../features/global-live-player/store/live-player-settings.store';

/**
 * Dropdown to open the live player: inline (miniatura) or in a new tab.
 */
export function LivePlayerToggle({ targetStationId }: { targetStationId?: string | null }) {
  const { formatMessage } = useIntl();
  const station = useActiveStation({ throwIfMissing: false });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const setStationId = useLivePlayerSettingsStore((state) => state.setStationId);

  const finalStationId = targetStationId ?? station?.id ?? null;
  const canOpenFullscreen = Boolean(station?.slug);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenFullscreen = () => {
    handleCloseMenu();
    if (station?.slug) {
      window.open(`/live-player/${station.slug}?fullscreen=true&autoplay=true`, '_blank');
    }
  };

  const handleOpenInline = () => {
    handleCloseMenu();
    if (finalStationId) {
      setStationId(finalStationId);
    }
  };

  return (
    <>
      <Tooltip
        title={formatMessage({
          id: 'livePlayer.toggle.tooltip',
          defaultMessage: 'Abrir LIVE',
        })}
      >
        <IconButton onClick={handleOpenMenu} size="small">
          <LiveTvIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        {...({
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          transformOrigin: { vertical: 'top', horizontal: 'right' },
        } as Pick<PopoverProps, 'anchorOrigin' | 'transformOrigin'>)}
      >
        <MenuItem onClick={handleOpenFullscreen} disabled={!canOpenFullscreen}>
          <LaunchIcon fontSize="small" sx={{ mr: 1.5 }} />
          {formatMessage({
            id: 'livePlayer.toggle.mode.fullscreen',
            defaultMessage: 'Abrir en pantalla completa',
          })}
        </MenuItem>
        <MenuItem onClick={handleOpenInline}>
          <PictureInPictureAltIcon fontSize="small" sx={{ mr: 1.5 }} />
          {formatMessage({
            id: 'livePlayer.toggle.mode.inline',
            defaultMessage: 'Abrir en miniatura',
          })}
        </MenuItem>
      </Menu>
    </>
  );
}
